import React, { useState, useEffect, useContext, memo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Close from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";

import { NotificationContext } from "../../../context/NotificationContext";
import {
  useLazyViewFileQuery,
  useLazyDownloadFileQuery,
  useDeleteFileMutation,
} from "../../../services/students";

const ClaimAwardDialog = ({
  open,
  onClose,
  onConfirm,
  award,
  onFilesUpdate,
}) => {
  const { addNotification } = useContext(NotificationContext);
  const [selectedNewFiles, setSelectedNewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  const [triggerView, { isFetching: isViewing }] = useLazyViewFileQuery();
  const [triggerDownload, { isFetching: isDownloading }] =
    useLazyDownloadFileQuery();
  const [deleteFile] = useDeleteFileMutation();

  useEffect(() => {
    if (award?.files) {
      setExistingFiles(award?.files);
    } else {
      setExistingFiles([]);
    }
    setSelectedNewFiles([]);
  }, [award, open]);

  const handleNewFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedNewFiles((prev) => [...prev, ...files]);
  };

  const removeNewFile = (index) => {
    setSelectedNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewExistingFile = async (fileId) => {
    try {
      const result = await triggerView(fileId).unwrap();
      if (result && result.success && result.url) {
        window.open(result.url, "_blank");
      } else {
        addNotification("Error retrieving file URL", "error");
      }
    } catch (error) {
      console.error("Error in handleViewExistingFile:", error);
      addNotification("Error retrieving file URL", "error");
    }
  };

  const handleDownloadExistingFile = async (fileId) => {
    try {
      const result = await triggerDownload(fileId).unwrap();
      if (result && result.success && result.url) {
        const link = document.createElement("a");
        link.href = result.url;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        addNotification("Error retrieving download URL", "error");
      }
    } catch (error) {
      console.error("Error in handleDownloadExistingFile:", error);
      addNotification("Error retrieving download URL", "error");
    }
  };

  const handleRemoveExistingFile = async (fileId) => {
    try {
      const result = await deleteFile(fileId).unwrap();
      if (result && result.success) {
        addNotification("File removed successfully!", "success");
        setExistingFiles((prev) => prev.filter((file) => file.id !== fileId));
        if (onFilesUpdate) onFilesUpdate();
      } else {
        addNotification("Error removing file", "error");
      }
    } catch (error) {
      console.error("Error removing file:", error);
      addNotification("Error removing file", "error");
    }
  };

  const handleConfirmClaim = () => {
    if (!selectedNewFiles.length || selectedNewFiles.length?.length === 0) {
      addNotification("Please attach at least one new file.", "error");
      return;
    }
    const formData = new FormData();
    selectedNewFiles.forEach((file) => {
      formData.append("files", file);
    });
    onConfirm({
      awardId: award?.id,
      files: selectedNewFiles,
    });    setSelectedNewFiles([]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Claim Award
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          You are about to claim the award:{" "}
          <strong>{award?.scholarship_name || "Award"}</strong>
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Already Attached Files:</Typography>
          {existingFiles && existingFiles?.length > 0 ? (
            existingFiles?.map((file, idx) => (
              <Box
                key={idx}
                sx={{ display: "flex", alignItems: "center", mt: 1 }}
              >
                <Typography variant="body2">
                  {file?.file_name} ({file?.file_type},{" "}
                  {(file?.file_size / 1024).toFixed(2)} KB)
                </Typography>
                <IconButton
                  onClick={() => handleViewExistingFile(file?.id)}
                  disabled={isViewing}
                >
                  {isViewing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  onClick={() => handleDownloadExistingFile(file?.id)}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton onClick={() => handleRemoveExistingFile(file?.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Typography variant="body2">No attached files.</Typography>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Attach New Files:</Typography>
          <TextField
            label="Upload Files"
            type="file"
            fullWidth
            InputLabelProps={{ shrink: true }} // Ensure the label is always displayed above the input field
            inputProps={{ multiple: true }}
            onChange={handleNewFileChange}
            sx={{ mt: 1 }}
          />
          {selectedNewFiles?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {selectedNewFiles?.map((file, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mt: 1 }}
                >
                  <Typography variant="body2">
                    {file?.name} ({file?.type}, {(file?.size / 1024).toFixed(2)}{" "}
                    KB)
                  </Typography>
                  <IconButton onClick={() => removeNewFile(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmClaim}
          variant="contained"
          color="primary"
          disabled={selectedNewFiles.length === 0}
        >
          Confirm Claim
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ClaimAwardDialog);
